import { useState, useEffect } from "react";
import { Loader2, X, RotateCcw, Clock } from "lucide-react";
import { api } from "../../lib/api";

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
}

export default function HistoryModal({ onClose, onRestored }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.getHistory().then(setItems).catch((e) => { setItems([]); setErr(e.message); });
  }, []);

  const restore = async (id) => {
    if (!window.confirm("Restore the site content to this version? Your current version is saved first, so you can undo this.")) return;
    setBusyId(id);
    setErr("");
    try {
      await api.restoreVersion(id);
      onRestored();
    } catch (e) {
      setErr(e.message);
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col cyber-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-[#00F0FF]" />
            <h3 className="text-white font-bold">Version history</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          <p className="text-slate-500 text-xs mb-4">
            Each save creates a restore point. Restoring brings back that version for all languages — your current version is saved first, so you can always undo.
          </p>

          {err && <p className="text-red-400 text-xs mb-3">{err}</p>}

          {items === null && <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#00F0FF]" size={24} /></div>}

          {items !== null && items.length === 0 && (
            <p className="text-slate-500 text-sm py-8 text-center">No previous versions yet. Versions appear here after you save changes.</p>
          )}

          <div className="space-y-2">
            {(items || []).map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 bg-[#0A0F2E] border border-white/10 rounded p-3">
                <div className="min-w-0">
                  <div className="text-slate-200 text-sm">{fmt(v.created_at)}</div>
                  <div className="text-slate-500 text-xs truncate">{v.note}{v.editor ? ` · ${v.editor}` : ""}</div>
                </div>
                <button
                  onClick={() => restore(v.id)}
                  disabled={busyId === v.id}
                  className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 text-xs border border-[#00F0FF]/30 text-[#00F0FF] rounded hover:bg-[#00F0FF]/10"
                >
                  {busyId === v.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
